import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
    Search, X, HelpCircle, MessageSquare, Download,
    CreditCard, Shield, User, Zap, ArrowRight, Headphones, BookOpen,
    Layers, ShieldCheck, Scale, FileText, Lock, Eye, Globe, UserCheck
} from 'lucide-react';
import { getHexColor, getSectionId, sortSections } from './legalPreviewUtils';

gsap.registerPlugin(ScrollTrigger);

const iconMap = {
    ShieldCheck,
    CreditCard,
    Eye,
    Lock,
    UserCheck,
    Zap,
    Globe,
    FileText,
    Search,
    Shield,
    Scale,
    HelpCircle,
    MessageSquare,
    Headphones,
    BookOpen,
    Download,
    User,
};

const ACCORDION_THEMES = [
    {
        name: 'cyan',
        card: 'bg-[#080b16]/80 border-white/[0.08] hover:border-cyber-cyan/50 hover:bg-gradient-to-br hover:from-[#061826]/90 hover:via-[#070e18]/95 hover:to-[#04060c] hover:shadow-[0_0_25px_rgba(0,240,255,0.2)]',
        badge: 'text-cyber-cyan bg-cyber-cyan/10 border-cyber-cyan/30',
        number: 'text-cyber-cyan',
        line: 'bg-gradient-to-r from-cyber-cyan to-cyber-purple',
    },
    {
        name: 'purple',
        card: 'bg-[#080b16]/80 border-white/[0.08] hover:border-cyber-purple/50 hover:bg-gradient-to-br hover:from-[#140824]/90 hover:via-[#0a0718]/95 hover:to-[#04060c] hover:shadow-[0_0_25px_rgba(189,0,255,0.2)]',
        badge: 'text-cyber-purple bg-cyber-purple/10 border-cyber-purple/30',
        number: 'text-cyber-purple',
        line: 'bg-gradient-to-r from-cyber-purple to-cyber-cyan',
    },
];

const VARIANT_CONFIG = {
    privacy: {
        prefix: 'privacy',
        badge: 'Legal & Security',
        title: 'Privacy',
        titleAccent: 'Policy',
        breadcrumb: 'Privacy Policy',
        sectionNoun: 'Policy ',
        defaultIcon: ShieldCheck,
        emptyTitle: 'No policy sections yet',
        emptyText: 'Check back soon for our full privacy documentation.',
        quickLinks: [
            {
                to: '/contact',
                icon: MessageSquare,
                color: 'text-cyber-pink',
                iconBg: 'bg-cyber-pink/10 border-cyber-pink/20',
                badgeColor: 'bg-cyber-pink',
                hoverEffect: 'hover:border-cyber-pink/50 hover:bg-[#200814]/60',
                label: 'Contact Support',
                desc: 'Need help with an order or privacy policy? Reach out to our dedicated support team.',
                btnText: 'Send Message',
            },
            {
                to: '/faq',
                icon: HelpCircle,
                color: 'text-cyber-cyan',
                iconBg: 'bg-cyber-cyan/10 border-cyber-cyan/20',
                badgeColor: 'bg-cyber-cyan',
                hoverEffect: 'hover:border-cyber-cyan/50 hover:bg-[#071424]/60',
                label: 'Frequently Asked Questions',
                desc: 'Quick answers about game licenses, digital delivery, security, and purchases.',
                btnText: 'View FAQs',
            },
            {
                to: '/terms',
                icon: Scale,
                color: 'text-cyber-purple',
                iconBg: 'bg-cyber-purple/10 border-cyber-purple/20',
                badgeColor: 'bg-cyber-purple',
                hoverEffect: 'hover:border-cyber-purple/50 hover:bg-[#130822]/60',
                label: 'Terms of Service',
                desc: 'Review our platform user agreement, fair play guidelines, and digital license terms.',
                btnText: 'View Terms',
            },
        ],
    },
    terms: {
        prefix: 'terms',
        badge: 'Legal Agreement',
        title: 'Terms of',
        titleAccent: 'Service',
        breadcrumb: 'Terms of Service',
        sectionNoun: 'Terms Articles',
        defaultIcon: FileText,
        emptyTitle: 'No terms sections yet',
        emptyText: 'Check back soon for our full terms documentation.',
        quickLinks: [
            {
                to: '/contact',
                icon: MessageSquare,
                color: 'text-cyber-pink',
                iconBg: 'bg-cyber-pink/10 border-cyber-pink/20',
                badgeColor: 'bg-cyber-pink',
                hoverEffect: 'hover:border-cyber-pink/50 hover:bg-[#200814]/60',
                label: 'Contact Support',
                desc: 'Need legal or terms assistance? Reach out to our dedicated support team.',
                btnText: 'Send Message',
            },
            {
                to: '/faq',
                icon: HelpCircle,
                color: 'text-cyber-cyan',
                iconBg: 'bg-cyber-cyan/10 border-cyber-cyan/20',
                badgeColor: 'bg-cyber-cyan',
                hoverEffect: 'hover:border-cyber-cyan/50 hover:bg-[#071424]/60',
                label: 'Frequently Asked Questions',
                desc: 'Quick answers about game licenses, digital delivery, security, and purchases.',
                btnText: 'View FAQs',
            },
            {
                to: '/privacy',
                icon: Shield,
                color: 'text-cyber-purple',
                iconBg: 'bg-cyber-purple/10 border-cyber-purple/20',
                badgeColor: 'bg-cyber-purple',
                hoverEffect: 'hover:border-cyber-purple/50 hover:bg-[#130822]/60',
                label: 'Privacy Policy',
                desc: 'How we collect, protect, and use your personal data and privacy rights.',
                btnText: 'View Policy',
            },
        ],
    },
};

export default function LegalDocumentPreview({
    variant = 'privacy',
    sections = [],
    embedded = false,
    loading = false,
    showQuickLinks = true,
    revealClass = '',
}) {
    const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.privacy;
    const [selectedCategory, setSelectedCategory] = useState('All');
    const pageContainerRef = useRef(null);
    const categoryScrollRef = useRef(null);
    const sortedSections = useMemo(() => sortSections(sections), [sections]);

    const normalizedSections = useMemo(() =>
        sortedSections.map((sec, idx) => ({
            id: getSectionId(config.prefix, sec, idx),
            num: sec.num || String(idx + 1).padStart(2, '0'),
            category: sec.subtitle?.trim() || sec.title?.trim() || 'General',
            title: sec.title || `Section ${idx + 1}`,
            subtitle: sec.subtitle || '',
            content: sec.content || '',
            icon: sec.icon || 'ShieldCheck',
            color: sec.color || 'text-cyber-cyan',
        })),
        [sortedSections, config.prefix]
    );

    const categories = useMemo(() => {
        const seen = new Set();
        const ordered = [];
        normalizedSections.forEach((s) => {
            const cat = s.subtitle ? s.subtitle.trim() : s.title.trim();
            if (cat && !seen.has(cat)) {
                seen.add(cat);
                ordered.push(cat);
            }
        });
        return ['All', ...ordered];
    }, [normalizedSections]);

    const filteredSections = useMemo(() => {
        return normalizedSections.filter((sec) => {
            const secCat = sec.subtitle ? sec.subtitle.trim() : sec.title.trim();
            return selectedCategory === 'All' || secCat === selectedCategory;
        });
    }, [normalizedSections, selectedCategory]);

    // Horizontal mouse wheel scrolling for category bar
    useEffect(() => {
        const el = categoryScrollRef.current;
        if (!el) return;

        const handleWheel = (e) => {
            if (el.scrollWidth > el.clientWidth) {
                if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                    e.preventDefault();
                    el.scrollLeft += e.deltaY;
                }
            }
        };

        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => el.removeEventListener('wheel', handleWheel);
    }, [categories]);

    // Desktop mouse drag-to-scroll support for category bar
    useEffect(() => {
        const el = categoryScrollRef.current;
        if (!el) return;

        let isDown = false;
        let startX = 0;
        let scrollLeft = 0;

        const onMouseDown = (e) => {
            isDown = true;
            startX = e.pageX - el.offsetLeft;
            scrollLeft = el.scrollLeft;
        };

        const onMouseLeave = () => {
            isDown = false;
        };

        const onMouseUp = () => {
            isDown = false;
        };

        const onMouseMove = (e) => {
            if (!isDown) return;
            e.preventDefault();
            const x = e.pageX - el.offsetLeft;
            const walk = (x - startX) * 1.5;
            el.scrollLeft = scrollLeft - walk;
        };

        el.addEventListener('mousedown', onMouseDown);
        el.addEventListener('mouseleave', onMouseLeave);
        el.addEventListener('mouseup', onMouseUp);
        el.addEventListener('mousemove', onMouseMove);

        return () => {
            el.removeEventListener('mousedown', onMouseDown);
            el.removeEventListener('mouseleave', onMouseLeave);
            el.removeEventListener('mouseup', onMouseUp);
            el.removeEventListener('mousemove', onMouseMove);
        };
    }, []);

    // Auto-scroll active category into view strictly within category bar container (never shifts page window)
    useEffect(() => {
        const container = categoryScrollRef.current;
        if (!container) return;

        const activeButton = container.querySelector('[data-active="true"]');
        if (!activeButton) return;

        const offsetLeft = activeButton.offsetLeft;
        const targetScrollLeft = offsetLeft - (container.clientWidth / 2) + (activeButton.clientWidth / 2);

        container.scrollTo({
            left: Math.max(0, targetScrollLeft),
            behavior: 'smooth',
        });
    }, [selectedCategory]);

    // Scroll-triggered animations for static bottom components (runs when content is ready)
    useEffect(() => {
        if (!pageContainerRef.current) return;
        if (loading) return;

        const ctx = gsap.context(() => {
            const bentoCards = pageContainerRef.current?.querySelectorAll('.support-bento-card');
            if (bentoCards && bentoCards.length > 0) {
                bentoCards.forEach((bento, idx) => {
                    gsap.fromTo(bento,
                        {
                            opacity: 0,
                            y: 20,
                        },
                        {
                            opacity: 1,
                            y: 0,
                            duration: 0.45,
                            delay: idx * 0.08,
                            ease: 'power2.out',
                            scrollTrigger: {
                                trigger: bento,
                                start: 'top 93%',
                                toggleActions: 'play none none none',
                                once: true,
                            }
                        }
                    );
                });
            }

            const banner = pageContainerRef.current?.querySelector('.support-banner-card');
            if (banner) {
                gsap.fromTo(banner,
                    {
                        opacity: 0,
                        y: 20,
                    },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.5,
                        ease: 'power2.out',
                        scrollTrigger: {
                            trigger: banner,
                            start: 'top 93%',
                            toggleActions: 'play none none none',
                            once: true,
                        }
                    }
                );
            }
        }, pageContainerRef);

        return () => ctx.revert();
    }, [loading]);

    // Refresh ScrollTrigger coordinates when list height updates
    useEffect(() => {
        const timer = setTimeout(() => {
            ScrollTrigger.refresh();
        }, 120);
        return () => clearTimeout(timer);
    }, [filteredSections, selectedCategory, loading]);


    return (
        <div ref={pageContainerRef} className={`min-h-screen bg-transparent text-gray-100 ${embedded ? 'py-4' : 'pt-24 pb-12 sm:pt-32 sm:pb-32'} relative overflow-hidden font-sans selection:bg-cyber-blue selection:text-black`}>
            {/* Dynamic Ambient Platform Lighting */}
            <div className="absolute top-0 left-0 w-full h-full bg-cyber-grid opacity-[0.04] pointer-events-none" />

            {/* Dynamic Center Halo Ambient Light */}
            <div
                className="absolute top-[8%] left-1/2 -translate-x-1/2 w-[70vw] max-w-[900px] h-[400px] rounded-full blur-[140px] transition-all duration-700 pointer-events-none opacity-20"
                style={{ backgroundColor: '#00f0ff' }}
            />

            <div className="absolute top-[30%] right-[-10%] w-[45vw] h-[45vw] max-w-[600px] bg-cyber-purple/10 rounded-full blur-[150px] pointer-events-none" />
            <div className="absolute bottom-[10%] left-[-10%] w-[45vw] h-[45vw] max-w-[600px] bg-cyber-blue/10 rounded-full blur-[150px] pointer-events-none" />

            <div className="container w-full relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
                {embedded && (
                    <div className="mb-4">
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyber-cyan/10 border border-cyber-cyan/20 text-[9px] font-cyber text-cyber-cyan uppercase tracking-widest">
                            Live Preview
                        </span>
                    </div>
                )}

                {/* Header */}
                <div className={`mb-6 sm:mb-8 flex flex-col items-start justify-between gap-4 sm:gap-6 relative z-50 ${revealClass}`}>
                    <div className="w-full header-title-animate">
                        <p className="text-[10px] font-cyber font-semibold text-cyber-cyan uppercase tracking-[0.25em] mb-2 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] animate-pulse"></span>
                            {config.badge}
                        </p>
                        <h1 className="font-cyber font-semibold text-2xl min-[375px]:text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight whitespace-normal break-words leading-tight">
                            {config.title} <span className="animate-gradient-text drop-shadow-[0_0_20px_rgba(0,240,255,0.35)]">{config.titleAccent}</span>
                        </h1>
                        <div className="h-[2px] w-16 sm:w-20 animate-gradient-bg mt-2.5 sm:mt-3 rounded-full"></div>
                    </div>
                </div>

                {/* High-Contrast Gradient Category Navigation Bar (Overflow-X Auto) */}
                <div className={`mb-8 category-bar-animate w-full max-w-full overflow-hidden ${revealClass}`}>
                    <div className="p-1.5 sm:p-2 rounded-2xl bg-[#080b16]/95 backdrop-blur-xl border border-white/[0.1] shadow-[0_10px_30px_rgba(0,0,0,0.6)] w-full max-w-full overflow-hidden">
                        <div
                            ref={categoryScrollRef}
                            data-lenis-prevent
                            className="flex items-center gap-2 sm:gap-2.5 overflow-x-auto no-scrollbar scroll-smooth p-1 w-full touch-pan-x overscroll-x-contain select-none"
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                        >
                            {loading ? (
                                [...Array(5)].map((_, idx) => (
                                    <div
                                        key={idx}
                                        className="flex-shrink-0 h-10 w-28 sm:w-32 rounded-xl bg-white/[0.04] border border-white/[0.06] animate-shimmer"
                                    />
                                ))
                            ) : (
                                categories.map((cat) => {
                                    const isSelected = selectedCategory === cat;
                                    const Icon = cat === 'All' ? Layers : config.defaultIcon;
                                    const count = cat === 'All'
                                        ? normalizedSections.length
                                        : normalizedSections.filter(s => {
                                            const secCat = s.subtitle ? s.subtitle.trim() : s.title.trim();
                                            return secCat === cat;
                                        }).length;

                                    return (
                                        <button
                                            key={cat}
                                            type="button"
                                            data-active={isSelected ? "true" : "false"}
                                            onClick={() => setSelectedCategory(cat)}
                                            className={`flex-shrink-0 min-w-max flex items-center gap-2 sm:gap-2.5 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl text-[11px] sm:text-xs font-cyber font-semibold tracking-wider uppercase transition-colors duration-150 group border ${isSelected
                                                ? 'bg-gradient-to-r from-[#20081e] via-[#101428] to-[#081a28] border-cyber-cyan text-white shadow-[0_0_15px_rgba(0,240,255,0.3)]'
                                                : 'bg-[#0a0d18]/80 border-white/[0.08] text-gray-300 hover:text-white hover:border-white/25 hover:bg-[#12172a]'
                                                }`}
                                        >
                                            <Icon className={`w-3.5 h-3.5 transition-transform duration-150 group-hover:scale-110 ${isSelected ? 'text-cyber-cyan' : 'text-gray-400 group-hover:text-cyber-cyan'}`} />
                                            <span className="whitespace-nowrap tracking-wide">{cat}</span>
                                            <span className={`px-1.5 sm:px-2 py-0.5 rounded-md text-[9px] sm:text-[10px] font-cyber font-semibold transition-colors duration-150 ${isSelected
                                                ? 'bg-cyber-cyan text-black shadow-[0_0_8px_rgba(0,240,255,0.4)]'
                                                : 'bg-white/10 text-gray-300 group-hover:text-white group-hover:bg-white/15'
                                                }`}>
                                                {count}
                                            </span>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                {/* Clean Cyberpunk Section Title Row */}
                <div className={`mb-6 flex items-center gap-3 faq-section-animate ${revealClass}`}>
                    <div className="w-1.5 h-4 sm:h-5 rounded-full bg-gradient-to-b from-cyber-cyan via-[#00c8ff] to-cyber-purple shadow-[0_0_10px_rgba(0,240,255,0.5)] flex-shrink-0" />
                    <h2 className="font-cyber font-bold text-xs sm:text-sm text-gray-200 tracking-wider uppercase flex items-center gap-2">
                        <span>{selectedCategory === 'All' ? `All ${config.sectionNoun}` : `${selectedCategory}`}</span>
                        <span className="text-[10px] font-cyber px-2 py-0.5 rounded-md bg-white/[0.05] border border-white/[0.08] text-gray-400">
                            {filteredSections.length}
                        </span>
                    </h2>
                    <div className="h-px flex-1 bg-gradient-to-r from-white/[0.08] via-white/[0.02] to-transparent" />
                </div>

                {/* Skeleton Loading State */}
                {loading ? (
                    <div className={`space-y-3 sm:space-y-3.5 mb-10 sm:mb-12 ${revealClass}`}>
                        {[...Array(4)].map((_, idx) => (
                            <div
                                key={idx}
                                className="w-full rounded-xl sm:rounded-2xl border border-white/[0.06] bg-[#080b16]/80 p-3.5 sm:p-4 animate-shimmer relative"
                            >
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-8 h-8 rounded-lg bg-white/[0.08]" />
                                    <div className="h-4 rounded bg-white/[0.09] w-[60%]" />
                                </div>
                                <div className="h-14 rounded-lg bg-white/[0.03] border border-white/[0.05]" />
                            </div>
                        ))}
                    </div>
                ) : filteredSections.length === 0 ? (
                    <div className={`py-16 px-4 text-center rounded-2xl bg-[#080b16]/50 border border-dashed border-white/10 mb-10 ${revealClass}`}>
                        <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-3 text-gray-400">
                            <HelpCircle className="w-6 h-6" />
                        </div>
                        <h3 className="font-cyber font-bold text-white uppercase text-base mb-1">No sections found</h3>
                        <p className="text-gray-400 text-xs font-sans mb-4 max-w-sm mx-auto">No policy clauses found for this category.</p>
                        <button
                            type="button"
                            onClick={() => setSelectedCategory('All')}
                            className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-gradient-to-r from-cyber-cyan to-cyber-purple text-black font-cyber font-bold uppercase tracking-wider shadow-[0_0_20px_rgba(0,240,255,0.35)] hover:scale-105 transition-all duration-200"
                        >
                            <span>View All Sections</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                    </div>
                ) : (
                    /* Full Width Alternating Cyberpunk List - Sleek & Compact (NOT Accordion) */
                    <div
                        key={selectedCategory}
                        className={`space-y-3 sm:space-y-3.5 mb-10 sm:mb-12 animate-faq-fade ${revealClass}`}
                    >
                        {filteredSections.map((section, index) => {
                            const theme = ACCORDION_THEMES[index % ACCORDION_THEMES.length];
                            const IconComponent = iconMap[section.icon] || config.defaultIcon;

                            return (
                                <div
                                    key={section.id || index}
                                    id={section.id}
                                    className={`w-full group rounded-xl sm:rounded-2xl border transition-all duration-300 overflow-hidden shadow-[0_4px_16px_rgba(0,0,0,0.4)] ${theme.card}`}
                                >
                                    {/* Sleek Compact Header - No Sec Count */}
                                    <div className="px-3.5 py-2.5 sm:px-5 sm:py-3.5 border-b border-white/[0.06] bg-gradient-to-r from-white/[0.03] via-transparent to-transparent flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
                                            {/* Compact Icon */}
                                            <div
                                                className="w-7 h-7 sm:w-9 sm:h-9 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105 shadow-[0_0_12px_rgba(0,0,0,0.3)]"
                                                style={{ color: getHexColor(section.color) }}
                                            >
                                                <IconComponent className="w-3.5 h-3.5 sm:w-4.5 sm:h-4.5" />
                                            </div>

                                            <div className="min-w-0 flex-1">
                                                {section.subtitle && (
                                                    <span className="text-[10px] font-cyber text-gray-400 uppercase tracking-wider block truncate mb-0.5">
                                                        {section.subtitle}
                                                    </span>
                                                )}
                                                <h3 className="text-xs sm:text-base font-cyber font-bold text-white uppercase tracking-tight break-words leading-snug">
                                                    {section.title}
                                                </h3>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Sleek Compact Content Area */}
                                    <div className="px-3.5 py-2.5 sm:px-5 sm:py-3.5">
                                        <div className="ql-snow">
                                            <div
                                                className="ql-editor text-xs sm:text-[13px] md:text-sm text-gray-300 font-sans leading-relaxed break-words px-0 py-0 [&_a]:text-cyber-cyan [&_a]:underline hover:[&_a]:text-white [&_strong]:text-white [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:space-y-1 [&_ul]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-4 [&_ol]:space-y-1 [&_ol]:my-1.5 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_h2]:text-white [&_h2]:font-cyber [&_h2]:text-sm [&_h2]:mt-2 [&_h2]:mb-1 [&_h3]:text-white [&_h3]:font-cyber [&_h3]:text-xs [&_h3]:mt-1.5 [&_h3]:mb-0.5 [&_iframe]:w-full [&_iframe]:max-w-full [&_img]:max-w-full [&_img]:h-auto [&_table]:w-full [&_table]:max-w-full [&_table]:block [&_table]:overflow-x-auto [&_pre]:overflow-x-auto [&_pre]:max-w-full [&_code]:break-all"
                                                dangerouslySetInnerHTML={{
                                                    __html: section.content ? section.content.replace(/&nbsp;|\u00a0/g, ' ') : '',
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Bottom Bento Support & Quick Action Links - Same as FAQ with hover effect and height */}
                {showQuickLinks && !embedded && (
                    <div className="mb-10 md600:mb-12 support-bento-animate">
                        <div className="grid grid-cols-1 md600:grid-cols-2 lg:grid-cols-3 gap-3.5 md600:gap-5">
                            {config.quickLinks.map((item) => {
                                const ItemIcon = item.icon;
                                return (
                                    <Link
                                        key={item.to}
                                        to={item.to}
                                        className={`group support-bento-card relative overflow-hidden p-5 sm:p-6 rounded-2xl bg-[#080b16]/80 border border-white/[0.08] ${item.hoverEffect} transition-all duration-300 flex flex-col justify-between hover:-translate-y-1.5 before:absolute before:top-0 before:-left-[100%] before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/[0.06] before:to-transparent before:transition-all before:duration-700 hover:before:left-[100%]`}
                                    >
                                        <div className="relative z-10">
                                            <div className={`w-11 h-11 rounded-xl ${item.iconBg} border flex items-center justify-center ${item.color} mb-4 transition-transform duration-300 group-hover:scale-105`}>
                                                <ItemIcon className="w-5 h-5" />
                                            </div>
                                            <h3 className="text-sm font-cyber font-bold text-white uppercase tracking-wider mb-1.5">
                                                {item.label}
                                            </h3>
                                            <p className="text-xs text-gray-400 font-sans leading-relaxed">
                                                {item.desc}
                                            </p>
                                        </div>
                                        <div className={`mt-5 pt-3 border-t border-white/[0.04] flex items-center justify-between text-xs font-cyber font-semibold ${item.color} uppercase tracking-wider relative z-10`}>
                                            <span>{item.btnText}</span>
                                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 transition-transform duration-300" />
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Support Banner - Perfectly Responsive (Matches FAQ Height & Styling) */}
                <div className="support-banner-card support-banner-animate p-6 sm:p-7 md:p-8 rounded-2xl bg-gradient-to-r from-[#14081e]/90 via-[#080b16]/95 to-[#061424]/90 border border-white/10 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden transition-all duration-300 hover:border-white/20 text-center md:text-left">
                    <div className="flex flex-col md:flex-row items-center gap-4 sm:gap-4.5 relative z-10 w-full md:w-auto flex-1 min-w-0">
                        <div className="w-12 h-12 rounded-2xl bg-cyber-cyan/10 border border-cyber-cyan/30 flex items-center justify-center text-cyber-cyan flex-shrink-0 shadow-[0_0_20px_rgba(0,240,255,0.15)]">
                            <Headphones className="w-6 h-6" />
                        </div>
                        <div className="flex-1 min-w-0 flex flex-col items-center md:items-start">
                            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-cyber-cyan/10 border border-cyber-cyan/20 text-[10px] font-cyber text-cyber-cyan uppercase tracking-wider mb-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan animate-pulse"></span>
                                24/7 Dedicated Support
                            </div>
                            <h3 className="font-cyber font-bold text-white text-base sm:text-lg md:text-xl uppercase tracking-tight mb-1.5">
                                Need Legal or Privacy Clarification?
                            </h3>
                            <p className="text-gray-400 font-sans text-xs sm:text-sm leading-relaxed max-w-lg">
                                Submit an inquiry and our legal and support team will assist you with data requests or policy details.
                            </p>
                        </div>
                    </div>

                    <div className="relative z-10 w-full sm:w-auto flex justify-center flex-shrink-0">
                        {!embedded ? (
                            <Link
                                to="/contact"
                                className="w-full sm:w-auto relative overflow-hidden inline-flex items-center justify-center gap-2.5 px-6 py-2.5 sm:px-7 sm:py-3 rounded-xl bg-gradient-to-r from-[#00f0ff] via-[#5c60ff] to-[#bd00ff] text-black font-cyber font-bold text-xs sm:text-[13px] uppercase tracking-wider hover:scale-105 active:scale-95 transition-all duration-300 whitespace-nowrap group before:absolute before:top-0 before:-left-[100%] before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/40 before:to-transparent before:transition-all before:duration-700 hover:before:left-[100%]"
                            >
                                <span className="relative z-10">Contact Support</span>
                                <ArrowRight className="w-4 h-4 relative z-10 group-hover:translate-x-1.5 transition-transform duration-300" />
                            </Link>
                        ) : (
                            <span className="w-full sm:w-auto relative overflow-hidden inline-flex items-center justify-center gap-2.5 px-6 py-2.5 sm:px-7 sm:py-3 rounded-xl bg-gradient-to-r from-[#00f0ff] via-[#5c60ff] to-[#bd00ff] text-black font-cyber font-bold text-xs sm:text-[13px] uppercase tracking-wider opacity-80 whitespace-nowrap">
                                <span>Contact Support</span>
                                <ArrowRight className="w-4 h-4" />
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
